import { queryOptions, useQuery, type UseQueryResult } from "@tanstack/react-query";
import { assetKeys } from "@/domains/assets/api/keys";
import type { Asset, AssetReservation } from "@/domains/assets/api/types";
import {
  fetchAllReservations,
  fetchAssetById,
  fetchAssets,
  fetchAvailableAssets,
  fetchReservationsByAsset,
} from "@/domains/assets/mocks/assetsService";

export const assetsListOptions = queryOptions({
  queryKey: assetKeys.lists(),
  queryFn: fetchAssets,
});

export const assetDetailOptions = (id: string) =>
  queryOptions({
    queryKey: assetKeys.detail(id),
    queryFn: () => fetchAssetById(id),
  });

export const reservationsOptions = queryOptions({
  queryKey: assetKeys.reservations(),
  queryFn: fetchAllReservations,
});

export const reservationsByAssetOptions = (assetId: string) =>
  queryOptions({
    queryKey: assetKeys.reservationsByAsset(assetId),
    queryFn: () => fetchReservationsByAsset(assetId),
  });

export const availableAssetsOptions = (startDate: string, endDate: string) =>
  queryOptions({
    queryKey: assetKeys.available(startDate, endDate),
    queryFn: () => fetchAvailableAssets(startDate, endDate),
    enabled: Boolean(startDate && endDate),
  });

export function useAssets(): UseQueryResult<ReadonlyArray<Asset>> {
  return useQuery(assetsListOptions);
}

export function useAsset(id: string): UseQueryResult<Asset | undefined> {
  return useQuery(assetDetailOptions(id));
}

export function useReservations(): UseQueryResult<ReadonlyArray<AssetReservation>> {
  return useQuery(reservationsOptions);
}

export function useReservationsByAsset(
  assetId: string,
): UseQueryResult<ReadonlyArray<AssetReservation>> {
  return useQuery(reservationsByAssetOptions(assetId));
}

export function useAvailableAssets(
  startDate: string,
  endDate: string,
): UseQueryResult<ReadonlyArray<Asset>> {
  return useQuery(availableAssetsOptions(startDate, endDate));
}
